/**
 * メールボックス横断一覧 `/mailboxes`。
 *
 * 運営がここを開く最大の理由は「乗っ取られたアカウントを止める」こと。
 * そのため 24h 送信数を上限との比率で見せ、超過行は destructive で目立たせる。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, Check, KeyRound, MoreHorizontal, PauseCircle, PlayCircle, Printer, Trash2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  CursorPagination,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  StatusBadge,
  Textarea,
  UsageBar,
  cn,
  formatBytes,
  formatCount,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { MAILBOX_STATUS, labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { ratioOf } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { DomainDetail, MailboxPasswordResetResult, MailboxSummary } from '../types/api.js';

const routeApi = getRouteApi(RID.mailboxes);

export function MailboxesPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canReset = usePermission(OPS_PERMISSION.supportMailboxReset);
  const canWrite = usePermission(OPS_PERMISSION.orgWrite);
  // ノードコマンド（クォータ再計算 / Maildir purge）は別権限。
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);

  const [resetTarget, setResetTarget] = React.useState<MailboxSummary | null>(null);
  const [quotaTarget, setQuotaTarget] = React.useState<MailboxSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<MailboxSummary | null>(null);
  const [recalcTarget, setRecalcTarget] = React.useState<MailboxSummary | null>(null);
  const [purgeTarget, setPurgeTarget] = React.useState<MailboxSummary | null>(null);

  const list = useCursorList<MailboxSummary>({
    path: '/mailboxes',
    keyFor: (params) => qk.mailboxes(params),
    filters: {
      q: search.q,
      status: search.status,
      organizationId: search.organizationId,
      domainId: search.domainId,
      overLimit: search.overLimit,
    },
  });

  const setSearch = (patch: Partial<typeof search>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  const columns: Array<DataTableColumn<MailboxSummary>> = [
    {
      id: 'address',
      header: 'メールアドレス',
      cell: (m) => (
        <div className="min-w-0">
          <span className="block truncate font-medium">{m.address}</span>
          <Link
            to="/domains/$domainId"
            params={{ domainId: m.domainId }}
            search={{ tab: 'overview' }}
            className="block truncate text-xs text-muted-foreground hover:text-primary"
          >
            {m.domainName}
          </Link>
        </div>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      hideBelow: 'md',
      cell: (m) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: m.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {m.organizationName}
        </Link>
      ),
    },
    { id: 'status', header: '状態', cell: (m) => <StatusBadge status={m.status} size="sm" /> },
    {
      id: 'quota',
      header: '使用容量',
      width: 'w-44',
      cell: (m) => <UsageBar used={m.usedBytes} total={m.quotaBytes} formatValue={(v) => formatBytes(v)} />,
    },
    { id: 'lastLogin', header: '最終ログイン', hideBelow: 'lg', cell: (m) => formatRelative(m.lastLoginAt) },
    { id: 'sent', header: '24h 送信', align: 'right', cell: (m) => <SentCell mailbox={m} /> },
    {
      id: 'webmail',
      header: 'Webメール',
      align: 'center',
      hideBelow: 'lg',
      cell: (m) =>
        m.webmailEnabled ? (
          <Check className="mx-auto size-4 text-success" aria-label="有効" />
        ) : (
          <span className="text-muted-foreground" aria-label="無効">
            —
          </span>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (m) => (
        <RowActions
          mailbox={m}
          canReset={canReset}
          canWrite={canWrite}
          canCommand={canCommand}
          onReset={setResetTarget}
          onQuota={setQuotaTarget}
          onDelete={setDeleteTarget}
          onRecalcQuota={setRecalcTarget}
          onPurge={setPurgeTarget}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="メールボックス"
        description="全契約組織のメールボックスを横断して表示します。"
        actions={<RefetchingDot active={list.query.isFetching && !list.query.isLoading} />}
      />

      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="メールアドレスで絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: {} })}
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFrom(MAILBOX_STATUS, labelOf),
            onChange: (v) => setSearch({ status: v as typeof search.status }),
          },
        ]}
      >
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={search.overLimit === true}
            onChange={(e) => setSearch({ overLimit: e.target.checked ? true : undefined })}
          />
          送信上限を超過中のみ
        </label>
      </FilterBar>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(m) => m.id}
        loading={list.query.isLoading}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="メールボックス"
            onClearFilter={() => void navigate({ search: {} })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <PasswordResetDialog mailbox={resetTarget} onClose={() => setResetTarget(null)} />
      <QuotaDialog mailbox={quotaTarget} onClose={() => setQuotaTarget(null)} />
      <DeleteDialog mailbox={deleteTarget} onClose={() => setDeleteTarget(null)} />
      <RecalcQuotaDialog mailbox={recalcTarget} onClose={() => setRecalcTarget(null)} />
      <PurgeDialog mailbox={purgeTarget} onClose={() => setPurgeTarget(null)} />
    </>
  );
}

/**
 * メールボックスが載っているノードを引く（R75）。
 *
 * `GET /ops/mailboxes` はノード ID を返さないので、`domainId` からドメインを
 * 1 件引いて `nodeId` を得る。**ドメインとメールボックスは必ず同じノードに載る**
 * （プロビジョニングが `domains.server_node_id` を見てメールボックスを配置する）
 * ので、これで宛先が確定する。ダイアログを開いたときだけ引く。
 */
function useMailboxNodeId(mailbox: MailboxSummary | null): {
  nodeId: string | null;
  loading: boolean;
  error: unknown;
} {
  const query = useQuery({
    queryKey: qk.domain(mailbox?.domainId ?? ''),
    queryFn: () => api.get<DomainDetail>(`/domains/${mailbox?.domainId ?? ''}`),
    enabled: mailbox !== null,
  });
  return { nodeId: query.data?.nodeId ?? null, loading: query.isLoading, error: query.error };
}

/**
 * クォータの再計算（`mailbox.recalc-quota`）。
 *
 * Dovecot の quota は差分更新なので、配送や削除が異常終了すると
 * 実体（Maildir）とズレる。ズレると「空にしたのに容量超過のまま送受信できない」
 * になり、**契約者からは何もできない**。運営が `doveadm quota recalc` 相当を
 * 撃つのが唯一の復旧手段なのに、これまで画面が無かった。
 */
function RecalcQuotaDialog({
  mailbox,
  onClose,
}: {
  mailbox: MailboxSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const { nodeId, loading, error } = useMailboxNodeId(mailbox);

  const mut = useMutation({
    mutationFn: () =>
      api.post(`/nodes/${nodeId ?? ''}/commands`, {
        kind: 'mailbox.recalc-quota',
        // **キーは `args`。** サーバーの zod は `.strict()` なので `payload` は 400。
        args: { address: mailbox?.address },
      }),
    onSuccess: onClose,
  });

  if (!mailbox) return null;

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="使用容量を再計算"
      description={`${mailbox.address} の Maildir を数え直して使用容量を更新します。送受信は止まりません。`}
      submitLabel="再計算する"
      pending={mut.isPending}
      disabled={nodeId === null}
      error={mut.isError ? mut.error : error ? error : undefined}
      onSubmit={() => mut.mutate()}
    >
      <NodeTargetNotice nodeId={nodeId} loading={loading} />
      <p className="text-xs text-muted-foreground">
        現在の表示値は {formatBytes(mailbox.usedBytes)}。実行結果はノード詳細の「コマンド履歴」に残ります。
      </p>
    </FormDialog>
  );
}

/**
 * Maildir の完全削除（`mailbox.purge`）。
 *
 * **メールの実体をノード上から消す。復元はバックアップからしかできない。**
 * 「メールボックスを削除」（論理削除 + 30 日の猶予）とは別物で、猶予を待たずに
 * 実体を落とす操作なので、確認テキストの入力を要求する。
 */
function PurgeDialog({
  mailbox,
  onClose,
}: {
  mailbox: MailboxSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const { nodeId, loading, error } = useMailboxNodeId(mailbox);

  const mut = useMutation({
    mutationFn: () =>
      api.post(`/nodes/${nodeId ?? ''}/commands`, {
        kind: 'mailbox.purge',
        args: { domain: mailbox?.domainName, localPart: mailbox?.localPart },
      }),
    onSuccess: onClose,
  });

  if (!mailbox) return null;

  return (
    <ConfirmTextDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Maildir を完全削除"
      description={`${mailbox.address} のメール本文をノード上から削除します。この操作は取り消せず、復元はバックアップからのリストアしかありません。`}
      confirmText={mailbox.address}
      confirmLabel="完全に削除する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : error ? error : undefined}
      onConfirm={() => {
        if (nodeId !== null) mut.mutate();
      }}
    >
      <NodeTargetNotice nodeId={nodeId} loading={loading} />
      <Alert variant="destructive">
        <AlertDescription>
          現在 {formatBytes(mailbox.usedBytes)} のメールが保存されています。
          解約後の後片付け以外では使わないでください。
        </AlertDescription>
      </Alert>
    </ConfirmTextDialog>
  );
}

/** コマンドの宛先ノード。引けなかったときに「押しても何も起きない」を避ける。 */
function NodeTargetNotice({
  nodeId,
  loading,
}: {
  nodeId: string | null;
  loading: boolean;
}): React.ReactElement {
  if (loading) return <p className="text-sm text-muted-foreground">対象ノードを確認しています…</p>;
  if (nodeId === null) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          このメールボックスのドメインはまだノードに割り当てられていません。コマンドの宛先が無いため実行できません。
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      対象ノード <code className="font-mono text-xs">{nodeId}</code>
    </p>
  );
}

function SentCell({ mailbox }: { mailbox: MailboxSummary }): React.ReactElement {
  const ratio = ratioOf(mailbox.sent24h, mailbox.dailySendLimit);
  const over = ratio !== null && ratio >= 1;
  const near = ratio !== null && ratio >= 0.8;
  return (
    <span className={cn('tabular text-sm', over ? 'text-destructive' : near ? 'text-warning' : undefined)}>
      {formatCount(mailbox.sent24h)}
      <span className="text-xs text-muted-foreground"> / {formatCount(mailbox.dailySendLimit)}</span>
    </span>
  );
}

/* -------------------------------------------------------------- 行アクション */

function RowActions({
  mailbox,
  canReset,
  canWrite,
  canCommand,
  onReset,
  onQuota,
  onDelete,
  onRecalcQuota,
  onPurge,
}: {
  mailbox: MailboxSummary;
  canReset: boolean;
  canWrite: boolean;
  canCommand: boolean;
  onReset: (m: MailboxSummary) => void;
  onQuota: (m: MailboxSummary) => void;
  onDelete: (m: MailboxSummary) => void;
  onRecalcQuota: (m: MailboxSummary) => void;
  onPurge: (m: MailboxSummary) => void;
}): React.ReactElement {
  const qc = useQueryClient();

  const submission = useMutation({
    mutationFn: (allow: boolean) => api.patch(`/mailboxes/${mailbox.id}`, { allowSubmission: allow }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.mailboxes() }),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`${mailbox.address} の操作`}>
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="truncate">{mailbox.address}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/*
          **設定情報シート（W-ONBOARD §3-4）。**「メールソフトの設定が
          分からない」はサポートで最も多い問い合わせで、そのとき契約者は
          コントロールパネルに辿り着けていないことが多い。運営が契約者へ
          渡すのと同じ紙をその場で出せると、電話口でポート番号を読み上げる
          必要が無くなる（読み上げは読み間違いを生み、二次問い合わせになる）。
          **パスワードは印字されない**ので、`orgRead` だけで出せる。
          印刷画面は別タブ（一覧の絞り込みを失わせない）。
        */}
        <DropdownMenuItem asChild>
          <a
            href={`/print/mail-setup?ids=${encodeURIComponent(mailbox.id)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Printer className="size-4" aria-hidden />
            設定情報を印刷（パスワードなし）
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {canReset ? (
          <DropdownMenuItem onSelect={() => onReset(mailbox)}>
            <KeyRound className="size-4" aria-hidden />
            パスワードをリセット
          </DropdownMenuItem>
        ) : null}
        {canWrite ? (
          <>
            <DropdownMenuItem onSelect={() => onQuota(mailbox)}>容量上限を変更</DropdownMenuItem>
            {mailbox.allowSubmission ? (
              <DropdownMenuItem
                className="text-destructive"
                onSelect={() => submission.mutate(false)}
                disabled={submission.isPending}
              >
                <PauseCircle className="size-4" aria-hidden />
                送信を一時停止（乗っ取り対応）
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => submission.mutate(true)} disabled={submission.isPending}>
                <PlayCircle className="size-4" aria-hidden />
                送信停止を解除
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onSelect={() => onDelete(mailbox)}>
              メールボックスを削除
            </DropdownMenuItem>
          </>
        ) : null}
        {canCommand ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
              ノード操作
            </DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => onRecalcQuota(mailbox)}>
              <Calculator className="size-4" aria-hidden />
              使用容量を再計算
            </DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onSelect={() => onPurge(mailbox)}>
              <Trash2 className="size-4" aria-hidden />
              Maildir を完全削除
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* --------------------------------------------------------- パスワードリセット */

function PasswordResetDialog({
  mailbox,
  onClose,
}: {
  mailbox: MailboxSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const [reason, setReason] = React.useState('');
  const [result, setResult] = React.useState<MailboxPasswordResetResult | null>(null);

  React.useEffect(() => {
    if (mailbox) setReason('');
  }, [mailbox]);

  /*
   * **サーバーの契約は `{ reason }` だけ**（`.strict()`・10 文字以上）。
   * 以前ここは `{ sendEmail }` を送っており必ず 400 で失敗していた。
   * 通知の有無は選べない（契約者への通知は必須で、送れなければ操作が失敗する）。
   *
   * この操作は `requireFreshAuth()` 付きなので、直近 5 分に 2 要素目を通して
   * いなければ再認証ダイアログ（`<ReauthGate>`）が挟まる。
   */
  const mut = useMutation({
    mutationFn: () =>
      api.post<MailboxPasswordResetResult>(
        `/orgs/${mailbox?.organizationId ?? ''}/mailboxes/${mailbox?.id ?? ''}/reset-password`,
        { reason: reason.trim() },
      ),
    onSuccess: (res) => {
      setResult(res);
      onClose();
    },
  });

  if (!mailbox && !result) return null;

  return (
    <>
      {mailbox ? (
        <FormDialog
          open
          onOpenChange={(next) => {
            if (!next) onClose();
          }}
          title="パスワードをリセット"
          description={`${mailbox.address} の一時パスワードを再発行します。既存セッションは失効します。`}
          submitLabel="リセットする"
          pending={mut.isPending}
          error={mut.isError ? mut.error : undefined}
          disabled={reason.trim().length < 10}
          onSubmit={() => mut.mutate()}
        >
          <Alert variant="warning">
            <AlertDescription>
              一時パスワードは<strong>契約者の連絡先へ直接メールで届きます</strong>。
              運営者の画面には表示されません（運営がチャットで平文を流す運用にしないための設計です）。
              契約者には必ず通知されます。
            </AlertDescription>
          </Alert>
          <Field
            label="理由"
            required
            hint="10 文字以上。問い合わせ番号など、後から追える情報を書いてください。監査ログと契約者への通知に残ります。"
          >
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          </Field>
        </FormDialog>
      ) : null}

      {/* 結果は「どこへ送ったか」だけを見せる。平文は受け取っていない。 */}
      <Dialog open={result !== null} onOpenChange={() => setResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>パスワードを再設定しました</DialogTitle>
            <DialogDescription>
              {result?.emailed
                ? `一時パスワードを ${result.deliveredTo ?? '契約者の連絡先'} へ送信しました。`
                : '一時パスワードのメール送信に失敗しました。契約者に連絡し、再度リセットしてください。'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setResult(null)}>
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------- 容量上限変更 */

function QuotaDialog({
  mailbox,
  onClose,
}: {
  mailbox: MailboxSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [gb, setGb] = React.useState('5');

  React.useEffect(() => {
    if (mailbox) setGb(String(Math.max(1, Math.round(mailbox.quotaBytes / 1024 ** 3))));
  }, [mailbox]);

  const mut = useMutation({
    mutationFn: () =>
      api.patch(`/mailboxes/${mailbox?.id ?? ''}`, { quotaBytes: Number(gb) * 1024 ** 3 }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.mailboxes() });
      onClose();
    },
  });

  if (!mailbox) return null;

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="容量上限を変更"
      description={`${mailbox.address}（現在の使用量 ${formatBytes(mailbox.usedBytes)}）`}
      submitLabel="変更する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={Number(gb) <= 0}
      onSubmit={() => mut.mutate()}
    >
      <Field
        label="容量上限（GB）"
        required
        hint="プランの既定値より小さくも大きくもできます。使用量を下回る値にすると即座に容量超過状態になります。"
      >
        <Input type="number" min={1} value={gb} onChange={(e) => setGb(e.target.value)} />
      </Field>
    </FormDialog>
  );
}

/* ------------------------------------------------------------------- 削除 */

function DeleteDialog({
  mailbox,
  onClose,
}: {
  mailbox: MailboxSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.del(`/mailboxes/${mailbox?.id ?? ''}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.mailboxes() });
      onClose();
    },
  });

  if (!mailbox) return null;

  return (
    <ConfirmTextDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="メールボックスを削除"
      description="削除予定にします。メールの実体は purge 期限（既定 30 日）まで保持され、その後に完全消去されます。"
      confirmText={mailbox.address}
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      onConfirm={() => mut.mutate()}
    />
  );
}
